"use client";

import * as React from "react";
import { CircleAlert as AlertCircle, Check, Cookie, ShieldCheck } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
export interface CookieConsent {
  analyticsEnabled: boolean;
  marketingEnabled: boolean;
  essential: boolean;
  updatedAt?: string;
}
export const COOKIE_STORAGE_KEY = "cookie_preferences";

/**
 * Authoritative reader for cookie consent.
 * Callable by application code or dynamically loaded tracking scripts.
 */
export function getCookieConsent(): CookieConsent {
  if (typeof window === "undefined") {
    return {
      analyticsEnabled: true,
      marketingEnabled: false,
      essential: true
    };
  }
  try {
    const saved = localStorage.getItem(COOKIE_STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      const analytics = typeof parsed.analyticsEnabled === "boolean" ? parsed.analyticsEnabled : typeof parsed.analytics === "boolean" ? parsed.analytics : true;
      const marketing = typeof parsed.marketingEnabled === "boolean" ? parsed.marketingEnabled : typeof parsed.marketing === "boolean" ? parsed.marketing : false;
      return {
        analyticsEnabled: analytics,
        marketingEnabled: marketing,
        essential: true,
        updatedAt: typeof parsed.updatedAt === "string" ? parsed.updatedAt : undefined
      };
    }
  } catch {
    // Return defaults when storage cannot be accessed
  }
  return {
    analyticsEnabled: true,
    marketingEnabled: false,
    essential: true
  };
}

/**
 * Gating function that future analytics or marketing scripts must call before loading.
 */
export function hasConsentFor(category: "analytics" | "marketing"): boolean {
  const consent = getCookieConsent();
  return category === "analytics" ? consent.analyticsEnabled : consent.marketingEnabled;
}

/**
 * Persists cookie preferences to the authoritative consent store.
 * Returns true on success, false if writing fails.
 */
export function saveCookieConsent(preferences: {
  analyticsEnabled: boolean;
  marketingEnabled: boolean;
}): boolean {
  if (typeof window === "undefined") return false;
  try {
    const payload = {
      analyticsEnabled: preferences.analyticsEnabled,
      marketingEnabled: preferences.marketingEnabled,
      analytics: preferences.analyticsEnabled,
      marketing: preferences.marketingEnabled,
      essential: true,
      updatedAt: new Date().toISOString()
    };
    localStorage.setItem(COOKIE_STORAGE_KEY, JSON.stringify(payload));
    return true;
  } catch {
    return false;
  }
}

// Attach helper functions to window for global access by any future external or inline scripts
if (typeof window !== "undefined") {
  (window as unknown as Record<string, unknown>).getCookieConsent = getCookieConsent;
  (window as unknown as Record<string, unknown>).hasConsentFor = hasConsentFor;
}
export default function CookiesPolicy() {
  const [analyticsEnabled, setAnalyticsEnabled] = React.useState(true);
  const [marketingEnabled, setMarketingEnabled] = React.useState(false);
  const [saveStatus, setSaveStatus] = React.useState<"idle" | "success" | "error">("idle");
  React.useEffect(() => {
    const consent = getCookieConsent();
    setAnalyticsEnabled(consent.analyticsEnabled);
    setMarketingEnabled(consent.marketingEnabled);
  }, []);
  const handleSave = () => {
    const success = saveCookieConsent({
      analyticsEnabled,
      marketingEnabled
    });
    if (success) {
      setSaveStatus("success");
    } else {
      setSaveStatus("error");
    }
    setTimeout(() => {
      setSaveStatus("idle");
    }, 3500);
  };
  return <section id="cookies-policy" data-nav="light" className="bg-background text-foreground py-16 sm:py-20">
      <div className="mx-auto max-w-[1136px] px-6">
        <div className="mx-auto max-w-3xl">
          {/* Signature Hairline Rule with Turquoise Terminal Dot */}
          <div className="relative mb-8 flex items-center">
            <div className="h-px w-full bg-border" />
            <div className="size-2 rounded-full bg-primary" />
          </div>

          {/* Heading and Date */}
          <div className="mb-10 space-y-3">
            <h1 className="font-default text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
              Cookie Policy
            </h1>
            <p className="text-sm font-medium text-muted-foreground">
              Last Updated: January 2025
            </p>
          </div>

          {/* Continuous Legal Text */}
          <div className="space-y-6 text-base leading-relaxed text-foreground/90">
            <p>
              This Cookies Policy explains how Hurghada Transfer Hub uses cookies
              and similar browser tracking technologies to ensure our website
              functions smoothly, stores session information, and analyzes user
              traffic patterns.
            </p>

            <div className="space-y-2">
              <h2 className="font-default text-lg font-semibold text-foreground">
                What Are Cookies?
              </h2>
              <p>
                Cookies are small text files placed on your computer or mobile
                device when you visit our website. They assist in remembering
                your selected transfer route preferences and improving overall
                website navigation.
              </p>
            </div>

            <div className="space-y-2">
              <h2 className="font-default text-lg font-semibold text-foreground">
                Essential Cookies
              </h2>
              <p>
                These cookies are strictly necessary for the technical operation
                of our booking form, security protocols, and session routing. You
                cannot disable essential cookies without impacting website
                functionality.
              </p>
            </div>

            <div className="space-y-2">
              <h2 className="font-default text-lg font-semibold text-foreground">
                Analytical &amp; Performance Cookies
              </h2>
              <p>
                We utilize privacy-compliant analytics to measure visitor
                engagement, popular transfer routes, and user page journeys to
                continually optimize our online services.
              </p>
            </div>
          </div>

          {/* Divider */}
          <div className="my-10 h-px w-full bg-border" />

          {/* Cookie Preferences Section */}
          <div className="rounded-md border border-border bg-card p-6 shadow-xs sm:p-8">
            <div className="mb-6 space-y-1.5">
              <h2 className="font-default text-xl font-semibold text-card-foreground">
                Your Cookie Preferences
              </h2>
              <p className="text-sm text-muted-foreground">
                Use the controls below to customize your accepted cookie
                categories. Your preferences will be saved for future visits on this device.
              </p>
            </div>

            <div className="divide-y divide-border">
              {/* Option: Essential */}
              <div className="flex items-center justify-between py-4">
                <div className="pr-4">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="size-4 text-primary" />
                    <span className="font-default text-sm font-medium text-foreground">
                      Essential Platform Cookies (Always Active)
                    </span>
                  </div>
                </div>
                <Switch checked={true} disabled aria-label="Essential platform cookies always active" />
              </div>

              {/* Option: Analytics */}
              <div className="flex items-center justify-between py-4">
                <div className="pr-4">
                  <div className="flex items-center gap-2">
                    <Cookie className="size-4 text-primary" />
                    <label htmlFor="analytics-cookie-toggle" className="cursor-pointer font-default text-sm font-medium text-foreground">
                      Performance &amp; Analytical Cookies
                    </label>
                  </div>
                </div>
                <Switch id="analytics-cookie-toggle" checked={analyticsEnabled} onCheckedChange={setAnalyticsEnabled} aria-label="Toggle performance and analytical cookies" />
              </div>

              {/* Option: Marketing */}
              <div className="flex items-center justify-between py-4">
                <div className="pr-4">
                  <div className="flex items-center gap-2">
                    <Cookie className="size-4 text-primary" />
                    <label htmlFor="marketing-cookie-toggle" className="cursor-pointer font-default text-sm font-medium text-foreground">
                      Marketing Cookies
                    </label>
                  </div>
                </div>
                <Switch id="marketing-cookie-toggle" checked={marketingEnabled} onCheckedChange={setMarketingEnabled} aria-label="Toggle marketing and route retargeting cookies" />
              </div>
            </div>

            {/* Actions */}
            <div className="mt-8 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
              <Button type="button" onClick={handleSave} className="w-full sm:w-auto">
                Save Cookie Preferences
              </Button>
              {saveStatus === "success" && <div className="flex items-center gap-1.5 text-xs font-medium text-primary">
                  <Check className="size-4 text-primary" />
                  <span>Preferences saved successfully.</span>
                </div>}
              {saveStatus === "error" && <div className="flex items-center gap-1.5 text-xs font-medium text-destructive">
                  <AlertCircle className="size-4 text-destructive" />
                  <span>Your preferences could not be saved. Please try again.</span>
                </div>}
            </div>
          </div>
        </div>
      </div>
    </section>;
}